import React from 'react';
import { Search, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui';
import { cn } from '../../lib/utils';
import { TEMAS, TIPS, buscarTemas } from './ayudaContent';

// Identidad MYKLUBB (esta pantalla es de la plataforma, no del club):
// lienzo oscuro + acento volt, como el resto del material de marca.
const VOLT = '#B3F000';
const COND = { fontFamily: "'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif" };
const BODY = { fontFamily: "'Barlow', system-ui, sans-serif" };

const FONTS_ID = 'myklubb-brand-fonts';
const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=Barlow:wght@400;500;700&display=swap';

const useBrandFonts = () => {
  React.useEffect(() => {
    if (document.getElementById(FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = FONTS_ID;
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }, []);
};

const Eyebrow = ({ children, className }) => (
  <p
    className={cn('text-[11px] font-bold uppercase tracking-[0.22em]', className)}
    style={{ ...BODY, color: VOLT }}
  >
    {children}
  </p>
);

const PARTES = TEMAS.reduce((acc, tema) => {
  if (!acc.includes(tema.parte)) acc.push(tema.parte);
  return acc;
}, []);

const SUGERENCIAS = ['pasar lista', 'plan de pago', 'estado de cuenta', 'tarjetón', 'uniformes', 'CURP'];

const TemaDetalle = ({ tema, onOpenTema }) => {
  if (!tema) return null;
  return (
    <div className="space-y-5" style={BODY}>
      {tema.intro && <p className="text-sm text-[#B8B8B8] leading-relaxed">{tema.intro}</p>}

      {tema.dolor && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#FF6B6B] mb-1.5">Sin MYKLUBB</p>
          <p className="text-sm italic text-[#9A9A9A] leading-relaxed">{tema.dolor}</p>
        </div>
      )}

      {tema.solucion && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8A8A8A] mb-2.5">
            Con MYKLUBB
          </p>
          <ul className="space-y-2.5">
            {tema.solucion.map((s) => (
              <li key={s.t} className="flex gap-3 text-sm leading-relaxed">
                <span
                  className="mt-[7px] size-[7px] rounded-[2px] shrink-0"
                  style={{ backgroundColor: VOLT }}
                  aria-hidden="true"
                />
                <span className="text-[#CFCFCF]">
                  <strong className="font-semibold text-white">{s.t}:</strong> {s.d}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tema.como && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] mb-2.5" style={{ color: VOLT }}>
            Cómo se hace, paso a paso
          </p>
          <ol className="grid gap-2">
            {tema.como.map((paso, i) => (
              <li
                key={paso}
                className="flex gap-3 items-baseline rounded-lg border border-[#3A3A3A] bg-[#252525] px-4 py-2.5 text-sm text-[#EDEDED] leading-relaxed"
              >
                <span className="text-lg font-extrabold tabular-nums shrink-0" style={{ ...COND, color: VOLT }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                {paso}
              </li>
            ))}
          </ol>
        </div>
      )}

      {tema.pasos && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8A8A8A] mb-2.5">
            Los 10 pasos — toca uno para ver cómo se hace
          </p>
          <ol className="grid gap-2">
            {tema.pasos.map((paso, i) => (
              <li key={paso.label || paso}>
                <button
                  type="button"
                  onClick={() => paso.id && onOpenTema && onOpenTema(paso.id)}
                  disabled={!paso.id || !onOpenTema}
                  className="group flex w-full items-baseline gap-3 rounded-lg border border-[#3A3A3A] bg-[#252525] px-4 py-2.5 text-left text-sm text-[#EDEDED] transition-colors enabled:hover:border-[#B3F000] enabled:hover:bg-[#2B2B2B] disabled:cursor-default"
                >
                  <span className="text-lg font-extrabold tabular-nums shrink-0" style={{ ...COND, color: VOLT }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="flex-1">{paso.label || paso}</span>
                  {paso.id && (
                    <span
                      className="shrink-0 self-center opacity-60 transition-opacity group-hover:opacity-100 text-xs font-bold uppercase tracking-[0.08em]"
                      style={{ color: VOLT }}
                      aria-hidden="true"
                    >
                      Ver cómo →
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tema.dia && (
        <div className="divide-y divide-[#2A2A2A]">
          {tema.dia.map((d) => (
            <div key={d.hora} className="flex gap-4 py-3 items-baseline">
              <span
                className="text-lg font-extrabold w-20 shrink-0 text-right leading-tight"
                style={{ ...COND, color: VOLT }}
              >
                {d.hora}
              </span>
              <span className="text-sm text-[#CFCFCF] leading-relaxed">{d.texto}</span>
            </div>
          ))}
        </div>
      )}

      {tema.planes && (
        <div className="overflow-x-auto rounded-xl border border-[#3A3A3A]">
          <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr className="text-left">
                {['Funcionalidad', 'Starter', 'Club', 'Pro'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.1em] border-b border-[#3A3A3A]"
                    style={{ color: VOLT }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tema.planes.map((fila) => (
                <tr key={fila.f} className="border-b border-[#2A2A2A] last:border-b-0">
                  <td className="px-4 py-2.5 text-white font-medium">{fila.f}</td>
                  {[fila.starter, fila.club, fila.pro].map((v, i) => (
                    <td
                      key={i}
                      className="px-4 py-2.5"
                      style={{ color: v === '✓' ? VOLT : '#8A8A8A', fontWeight: v === '✓' ? 700 : 400 }}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tema.beneficio && (
        <div
          className="rounded-xl px-4 py-3.5"
          style={{ backgroundColor: 'rgba(179,240,0,0.06)', border: '1px solid rgba(179,240,0,0.25)' }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] mb-1" style={{ color: VOLT }}>
            Lo que ganas
          </p>
          <p className="text-sm text-[#E9FFC2] leading-relaxed">{tema.beneficio}</p>
        </div>
      )}
    </div>
  );
};

const CentroAyuda = () => {
  useBrandFonts();
  const [query, setQuery] = React.useState('');
  const [temaAbierto, setTemaAbierto] = React.useState(null);
  const [parteExpandida, setParteExpandida] = React.useState(null);

  const resultados = React.useMemo(() => buscarTemas(query), [query]);
  const buscando = query.trim().length >= 2;
  const indiceActual = temaAbierto ? TEMAS.findIndex((t) => t.id === temaAbierto.id) : -1;

  const abrirTema = (id) => {
    const tema = TEMAS.find((t) => t.id === id);
    if (tema) setTemaAbierto(tema);
  };

  return (
    <FeaturedLayout title="Centro de ayuda" subtitle="Busca tu duda o explora qué resuelve cada módulo">
      <div className="-m-4 lg:-m-6 min-h-[calc(100vh-4rem)] bg-[#0D0D0D] text-white" style={BODY}>
        {/* ===== Hero con buscador ===== */}
        <div
          className="px-6 pt-12 pb-10 text-center"
          style={{
            background:
              'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(179,240,0,0.10), transparent 70%)',
          }}
        >
          <img
            src="/assets/myklubb-logo-v-light.svg"
            alt="MYKLUBB"
            className="h-20 md:h-24 mx-auto"
          />
          <Eyebrow className="mt-7 mb-3">Centro de ayuda · Clubes</Eyebrow>
          <h1
            className="text-4xl md:text-6xl font-extrabold uppercase leading-none tracking-tight"
            style={COND}
          >
            ¿En qué te
            <br />
            ayudamos hoy?
          </h1>
          <p className="max-w-md mx-auto mt-4 text-[15px] text-[#8A8A8A]">
            Busca tu duda y te decimos qué la resuelve, cómo y qué ganas con eso.
          </p>

          {/* Buscador */}
          <div className="relative max-w-xl mx-auto mt-8 text-left">
            <div className="flex items-center gap-3 rounded-full border border-[#3A3A3A] bg-[#1A1A1A] pl-5 pr-2 py-1.5 transition-all focus-within:border-[#B3F000] focus-within:shadow-[0_0_0_3px_rgba(179,240,0,0.15)]">
              <Search className="size-4 shrink-0 text-[#5A5A5A]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='"pasar lista", "estado de cuenta", "tarjetón"…'
                aria-label="Buscar en el centro de ayuda"
                className="flex-1 min-w-0 bg-transparent py-2.5 text-base text-white placeholder:text-[#5A5A5A] outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="shrink-0 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-[#8A8A8A] hover:text-white"
                >
                  Limpiar
                </button>
              )}
            </div>

            {/* Resultados */}
            {buscando && (
              <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-30 overflow-hidden rounded-2xl border border-[#3A3A3A] bg-[#1A1A1A] shadow-[0_24px_60px_rgba(0,0,0,0.6)]">
                <p className="px-5 pt-3.5 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#5A5A5A]">
                  {resultados.length === 0
                    ? 'Sin resultados'
                    : `${resultados.length} resultado${resultados.length === 1 ? '' : 's'}`}
                </p>
                {resultados.length === 0 ? (
                  <p className="px-5 pb-5 text-sm text-[#8A8A8A]">
                    No encontramos nada con <strong className="text-white">"{query.trim()}"</strong>.
                    Prueba con otra palabra o explora los temas aquí abajo.
                  </p>
                ) : (
                  <div className="max-h-[50vh] overflow-y-auto">
                    {resultados.slice(0, 10).map((tema) => (
                      <button
                        key={tema.id}
                        type="button"
                        onClick={() => abrirTema(tema.id)}
                        className="block w-full border-t border-[#242424] px-5 py-3 text-left transition-colors hover:bg-[#252525] focus-visible:bg-[#252525] outline-none"
                      >
                        <span className="flex items-baseline gap-2.5">
                          <span className="text-[15px] font-extrabold shrink-0" style={{ ...COND, color: VOLT }}>
                            {tema.num}
                          </span>
                          <span className="text-[17px] font-bold leading-snug" style={COND}>
                            {tema.titulo}
                          </span>
                          <span className="ml-auto shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-[#5A5A5A]">
                            {tema.parte}
                          </span>
                        </span>
                        {(tema.dolor || tema.intro) && (
                          <span className="mt-0.5 block pl-7 text-[13px] text-[#8A8A8A] line-clamp-2">
                            {tema.dolor || tema.intro}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sugerencias */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#5A5A5A]">
                Populares:
              </span>
              {SUGERENCIAS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setQuery(s)}
                  className="rounded-full border border-[#3A3A3A] px-3.5 py-1 text-[13px] text-[#8A8A8A] transition-colors hover:border-[#8A8A8A] hover:text-white"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ===== Contenido ===== */}
        <div className="mx-auto max-w-3xl px-5 pb-16 space-y-12">
          {/* Tips */}
          <section>
            <Eyebrow>Tips por tema</Eyebrow>
            <h2 className="mt-1 text-[26px] font-bold uppercase leading-tight" style={COND}>
              Lo que más se pregunta
            </h2>
            <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
              {TIPS.map((grupo) => (
                <div key={grupo.tema} className="rounded-xl border border-[#3A3A3A] bg-[#1A1A1A] p-5 pb-3">
                  <h3
                    className="mb-2 text-[16px] font-bold uppercase tracking-[0.06em]"
                    style={{ ...COND, color: VOLT }}
                  >
                    {grupo.tema}
                  </h3>
                  <ul>
                    {grupo.items.map((item, i) => (
                      <li key={item.q}>
                        <button
                          type="button"
                          onClick={() => abrirTema(item.id)}
                          className={cn(
                            'flex w-full items-baseline gap-2.5 rounded-md px-1 py-2 text-left text-[14px] text-[#CFCFCF] transition-colors hover:bg-[#252525] hover:text-white',
                            i > 0 && 'border-t border-[#242424]'
                          )}
                        >
                          <span className="shrink-0 font-bold" style={{ color: VOLT }} aria-hidden="true">
                            →
                          </span>
                          {item.q}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Índice */}
          <section>
            <Eyebrow>Explora el manual</Eyebrow>
            <h2 className="mt-1 text-[26px] font-bold uppercase leading-tight" style={COND}>
              Todos los temas
            </h2>
            <div className="mt-5 space-y-3">
              {PARTES.map((parte) => {
                const temasDeParte = TEMAS.filter((t) => t.parte === parte);
                const abierta = parteExpandida === parte;
                return (
                  <div key={parte} className="overflow-hidden rounded-xl border border-[#3A3A3A] bg-[#1A1A1A]">
                    <button
                      type="button"
                      onClick={() => setParteExpandida(abierta ? null : parte)}
                      aria-expanded={abierta}
                      className="flex w-full items-center gap-4 px-5 py-4 text-left"
                    >
                      <span className="text-[20px] font-bold uppercase leading-none" style={COND}>
                        {parte}
                      </span>
                      <span className="ml-auto shrink-0 text-[11px] font-bold uppercase tracking-[0.08em] text-[#5A5A5A]">
                        {temasDeParte.length} tema{temasDeParte.length === 1 ? '' : 's'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'size-4 shrink-0 text-[#5A5A5A] transition-transform duration-200',
                          abierta && 'rotate-180'
                        )}
                      />
                    </button>
                    {abierta && (
                      <div className="border-t border-[#242424] px-2.5 py-2">
                        {temasDeParte.map((tema) => (
                          <button
                            key={tema.id}
                            type="button"
                            onClick={() => abrirTema(tema.id)}
                            className="flex w-full items-baseline gap-3 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-[#252525]"
                          >
                            <span
                              className="w-7 shrink-0 text-[16px] font-extrabold tabular-nums"
                              style={{ ...COND, color: VOLT }}
                            >
                              {tema.num}
                            </span>
                            <span className="flex-1 text-[15px] text-[#EDEDED]">{tema.titulo}</span>
                            <span className="hidden shrink-0 rounded-full border border-[#3A3A3A] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#8A8A8A] sm:inline">
                              {tema.chip}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Footer de marca */}
          <div className="pt-2 text-center">
            <img src="/assets/myklubb-icon.svg" alt="" className="mx-auto h-9" aria-hidden="true" />
            <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-[#5A5A5A]">
              Where Clubs Perform.
            </p>
          </div>
        </div>
      </div>

      {/* ===== Visor de tema ===== */}
      <Dialog open={!!temaAbierto} onOpenChange={(open) => { if (!open) setTemaAbierto(null); }}>
        <DialogContent className="max-w-2xl flex flex-col gap-0 overflow-hidden border-[#3A3A3A] bg-[#1A1A1A] text-white p-0">
          {temaAbierto && (
            <>
              <div className="shrink-0 border-b border-[#242424] px-6 pt-5 pb-4">
                <DialogDescription
                  className="text-[11px] font-bold uppercase tracking-[0.14em]"
                  style={{ ...BODY, color: VOLT }}
                >
                  {temaAbierto.parte}
                </DialogDescription>
                <DialogTitle
                  className="mt-1 flex items-baseline gap-3 text-left text-[26px] font-bold uppercase leading-tight text-white"
                  style={COND}
                >
                  <span className="shrink-0" style={{ color: VOLT }}>{temaAbierto.num}</span>
                  {temaAbierto.titulo}
                </DialogTitle>
                <span className="mt-2 inline-block rounded-full border border-[#3A3A3A] px-3 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A8A]">
                  {temaAbierto.chip}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <TemaDetalle tema={temaAbierto} onOpenTema={abrirTema} />
              </div>
              <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[#242424] px-6 py-3.5">
                <button
                  type="button"
                  disabled={indiceActual <= 0}
                  onClick={() => setTemaAbierto(TEMAS[indiceActual - 1])}
                  className="flex items-center gap-1 rounded-full border border-[#3A3A3A] px-4 py-1.5 text-[13px] font-bold uppercase tracking-[0.06em] text-[#8A8A8A] transition-colors hover:text-white hover:bg-[#252525] disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-[#8A8A8A]"
                  style={COND}
                >
                  <ChevronLeft className="size-4" />
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={indiceActual === -1 || indiceActual >= TEMAS.length - 1}
                  onClick={() => setTemaAbierto(TEMAS[indiceActual + 1])}
                  className="flex items-center gap-1 rounded-full border border-[#3A3A3A] px-4 py-1.5 text-[13px] font-bold uppercase tracking-[0.06em] text-[#8A8A8A] transition-colors hover:text-white hover:bg-[#252525] disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-[#8A8A8A]"
                  style={COND}
                >
                  Siguiente
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </FeaturedLayout>
  );
};

export default CentroAyuda;
